import { useState, type FormEvent } from "react";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { Button } from "@/components/ui/Button";
import { MediaGrid } from "@/components/media/MediaGrid";
import { MediaUploader } from "@/components/media/MediaUploader";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { useDeleteMedia, useMedia, useUpdateMedia } from "@/features/media/hooks";
import type { MediaItem } from "@/features/media/types";

export function MediaPage() {
  const currentUser = useCurrentUser();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<MediaItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);
  const [form, setForm] = useState({ originalName: "", altText: "" });
  const media = useMedia({ page: 1, pageSize: 48, search });
  const updateMedia = useUpdateMedia();
  const deleteMedia = useDeleteMedia();
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");

  function openEdit(item: MediaItem) {
    setEditing(item);
    setForm({ originalName: item.originalName, altText: item.altText ?? "" });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    updateMedia.mutate(
      { id: editing.id, input: { originalName: form.originalName, altText: form.altText || null } },
      { onSuccess: () => setEditing(null) },
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Media Library" description="Upload and manage reusable site images." />
      <MediaUploader />
      <input
        className="h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by filename, display name, or alt text"
      />
      <MediaGrid
        items={media.data?.items ?? []}
        isLoading={media.isLoading}
        canDelete={canDelete}
        onEdit={openEdit}
        onDelete={(item) => setDeleteTarget(item)}
      />
      <AdminFormDrawer isOpen={editing !== null} title="Edit Media" onClose={() => setEditing(null)}>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Display Name" value={form.originalName} onChange={(value) => setForm((current) => ({ ...current, originalName: value }))} />
          <TextField label="Alt Text" value={form.altText} onChange={(value) => setForm((current) => ({ ...current, altText: value }))} />
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" disabled={updateMedia.isPending}>{updateMedia.isPending ? "Saving..." : "Save"}</Button>
          </div>
        </form>
      </AdminFormDrawer>
      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Media"
        description={`Delete "${deleteTarget?.originalName ?? "this media item"}" from the library? Existing content may still reference the file URL.`}
        isPending={deleteMedia.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMedia.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })}
      />
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
